import React from 'react';
import { Shield, Database, FileText, ExternalLink } from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
      </div>

      {/* Introduction */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2" />
          Service Agreement
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          By accessing and using ZippyHealth Tracker ("the Service"), you agree to be bound by these Terms of Service 
          and all applicable laws and regulations. The Service provides health tracking capabilities including medication 
          management and nutritional information for informational purposes only.
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            <strong>Medical Disclaimer:</strong> This service is not a substitute for professional medical advice, 
            diagnosis, or treatment. Always consult your healthcare provider.
          </p>
        </div>
      </section>

      {/* Data Sources and Accuracy */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="w-5 h-5 mr-2" />
          Data Sources and Information Accuracy
        </h2>
        
        <div className="space-y-4 text-gray-700 text-sm leading-relaxed">
          <p>
            <strong>Information Compilation:</strong> The Service aggregates information from multiple public and 
            private databases, research studies, and government sources. All information is provided "as is" without 
            warranty of any kind.
          </p>
          
          <p>
            <strong>Nutritional Data:</strong> Food and nutritional information is estimated based on data compiled 
            from various sources including but not limited to analytical studies, published literature, commercial 
            food labeling, dietary surveys, and laboratory analyses. Individual food items may vary significantly 
            from displayed values due to preparation methods, brand variations, seasonal changes, and other factors.
          </p>
          
          <p>
            <strong>Medication Information:</strong> Pharmaceutical data is derived from regulatory databases and 
            published sources. Users must verify all medication information with licensed healthcare providers and 
            official prescribing information.
          </p>

          {/* Detailed Attribution - Buried in Legal Text */}
          <div className="border-l-4 border-gray-300 pl-4 mt-6 text-xs text-gray-600">
            <p className="font-medium mb-2">Data Source Attribution (Section 4.2.1):</p>
            <p className="mb-2">
              Nutritional information is compiled from the following primary sources: (a) United States Department 
              of Agriculture (USDA) FoodData Central database including Foundation Foods (laboratory-analyzed 
              commodity foods), SR Legacy Foods (historical nutritional database), Survey Foods from the Food 
              and Nutrient Database for Dietary Studies (FNDDS) as used in What We Eat in America NHANES, 
              Branded Foods obtained through public-private partnerships with commercial food manufacturers, 
              and Experimental Foods from peer-reviewed research publications; (b) Additional nutritional 
              data from published scientific literature, commercial food labeling databases, and analytical 
              studies conducted by various research institutions.
            </p>
            <p className="mb-2">
              Pharmaceutical information is sourced from: (a) United States Food and Drug Administration (FDA) 
              National Drug Code (NDC) Directory containing registration data for all FDA-approved medications; 
              (b) Published prescribing information and drug labeling data; (c) Pharmacological databases and 
              reference materials.
            </p>
            <p>
              All data sources are subject to their respective terms of use and licensing agreements. The Service 
              operates under applicable fair use provisions and public domain licensing where available.
            </p>
          </div>
        </div>
      </section>

      {/* User Responsibilities */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          User Responsibilities
        </h2>
        <div className="space-y-3 text-gray-700">
          <p>
            <strong>Verification Requirement:</strong> Users are solely responsible for verifying the accuracy 
            of all information before making health-related decisions. This includes confirming medication 
            details with healthcare providers and validating nutritional information against product labels.
          </p>
          <p>
            <strong>Professional Consultation:</strong> Users must consult qualified healthcare professionals 
            for medical advice, medication management, and dietary planning. The Service does not replace 
            professional medical care.
          </p>
          <p>
            <strong>Data Accuracy:</strong> While we strive for accuracy, users acknowledge that information 
            may be incomplete, outdated, or incorrect. Users should not rely solely on the Service for 
            critical health decisions.
          </p>
        </div>
      </section>

      {/* Limitations and Disclaimers */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Limitations and Disclaimers</h2>
        <div className="space-y-3 text-gray-700 text-sm">
          <p>
            <strong>No Warranty:</strong> The Service is provided "as is" without warranties of any kind, 
            either express or implied, including but not limited to warranties of merchantability, fitness 
            for a particular purpose, or non-infringement.
          </p>
          <p>
            <strong>Limitation of Liability:</strong> In no event shall ZippyHealth Tracker be liable for 
            any direct, indirect, incidental, special, or consequential damages arising from the use of 
            the Service or reliance on the information provided.
          </p>
          <p>
            <strong>Data Updates:</strong> Information is updated periodically but may not reflect the most 
            current data available from source databases. Update frequencies vary by data source and type.
          </p>
        </div>
      </section>

      {/* Privacy and Data Handling */}
      <section className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Privacy and Data Handling</h2>
        <div className="space-y-3 text-gray-700">
          <p>
            User data is handled in accordance with our Privacy Policy. Health information is considered 
            sensitive and is protected using industry-standard security measures.
          </p>
          <p>
            The Service may log usage patterns and search queries for improving functionality and user 
            experience. No personally identifiable health information is shared with third parties without 
            explicit user consent.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="bg-gray-50 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Questions or Concerns?</h3>
        <p className="text-gray-600 mb-4">
          If you have questions about these Terms of Service or need clarification on any provisions, 
          please contact our support team.
        </p>
        <div className="flex justify-center space-x-4 text-sm">
          <a href="mailto:support@zippyhealth.io" className="text-blue-600 hover:text-blue-700 flex items-center">
            <ExternalLink className="w-4 h-4 mr-1" />
            Email Support
          </a>
          <span className="text-gray-400">|</span>
          <a href="/privacy" className="text-blue-600 hover:text-blue-700">
            Privacy Policy
          </a>
        </div>
      </section>

      {/* Effective Date */}
      <div className="text-center text-sm text-gray-500">
        <p>These Terms of Service are effective as of the date last updated above.</p>
        <p>Continued use of the Service constitutes acceptance of any updates to these terms.</p>
      </div>
    </div>
  );
};

export default TermsOfService; 
